import React, { useEffect, useRef, useState } from 'react';

import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useRefreshTokenMutation } from '../../../store/queries/auth';
import { useGetProfileQuery } from '../../../store/queries/users';
import { signedOut } from '../../../store/slices/auth';
import { AnalyticsEvents, trackEvent } from '../../../utils/analytics';
import { captureSessionSource, flushSessionSource } from '../../../utils/attribution';
import { storeReferralCode } from '../../../utils/referral';
import { setSentryUser } from '../../../utils/sentry';
import { setAccessToken } from '../../../utils/token-store';
import { LoadingIndicator } from '../loading-indicator';

/**
 * Proactively refreshes the access token on app startup if the user was previously logged in.
 * This prevents the 401 → refresh → retry cycle on every page load that occurs because
 * the access token is stored in memory and lost on refresh, while isLoggedIn persists in localStorage.
 */
const AppInitializer = ({ children }) => {
  const isImpersonating = window.location.pathname === '/impersonate';
  const needsRefresh = localStorage.getItem('isLoggedIn') === 'true' && !isImpersonating;
  const [ready, setReady] = useState(() => !needsRefresh);
  const fetchedRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const ref = searchParams.get('ref');

    if (ref?.trim()) {
      storeReferralCode(ref.trim());
      const cleaned = new URLSearchParams(searchParams);
      cleaned.delete('ref');
      setSearchParams(cleaned, { replace: true });
    }

    captureSessionSource(searchParams);

    const utmSource = searchParams.get('utm_source');
    if (utmSource === 'email') {
      trackEvent(AnalyticsEvents.EMAIL_LINK_CLICKED, {
        medium: searchParams.get('utm_medium') || 'unknown',
        campaign: searchParams.get('utm_campaign') || 'unknown',
      });
    }
  }, [searchParams, setSearchParams]);

  const dispatch = useDispatch();
  const [refreshToken] = useRefreshTokenMutation();

  useEffect(() => {
    if (ready || fetchedRef.current) return;

    fetchedRef.current = true;

    refreshToken()
      .unwrap()
      .then((data) => {
        if (data?.access_token) {
          setAccessToken(data.access_token);
        } else {
          dispatch(signedOut());
        }
      })
      .catch(() => {
        dispatch(signedOut());
      })
      .finally(() => setReady(true));
  }, [dispatch, ready, refreshToken]);

  // The captured source can only be attributed once the refresh above has put a
  // token in memory, so this runs after `ready` rather than alongside the parse.
  useEffect(() => {
    if (!ready) return;

    flushSessionSource();
  }, [ready]);

  const { data: profileData } = useGetProfileQuery(undefined, { skip: !ready || !needsRefresh });

  useEffect(() => {
    setSentryUser(profileData || null);
  }, [profileData]);

  if (!ready)
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <LoadingIndicator />
      </div>
    );

  return children;
};

export default AppInitializer;
